import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingScreen } from '@lendica/components';
import { useCompanyNavigate } from '@lendica/hooks';

interface OverlaySidebarProps {
    children: ReactNode;
    loading?: boolean;
    redirectRoute: string;
    open: boolean;
}

export const OverlaySidebar = ({ children, loading, redirectRoute, open }: OverlaySidebarProps) => {
    const navigate = useCompanyNavigate();

    if (!open) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300, // Above MUI modal backdrop
                display: 'flex',
            }}
        >
            {/* Backdrop */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(2px)',
                }}
                onClick={() => navigate(redirectRoute)}
            />

            {/* Sidebar Panel */}
            <Slide
                direction="left"
                in={open}
                mountOnEnter
                unmountOnExit
                easing={{
                    enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        marginLeft: 'auto',
                        width: {
                            xs: '100%', // Full width on mobile
                            sm: '100%', // Full width on small tablets
                            md: '66.666%', // 2/3 width on desktop
                            lg: '66.666%',
                            xl: '50%',
                        },
                        height: '100vh',
                        backgroundColor: 'background.paper',
                        boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        // overflow: 'hidden',
                    }}
                >
                    {/* Header with close button */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: {
                                xs: 64,
                                sm: 64,
                                md: 10,
                                lg: 10,
                                xl: 10,
                            },
                            left: {
                                xs: 14,
                                sm: 14,
                                md: -48,
                                lg: -48,
                                xl: -48,
                            },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => navigate(redirectRoute)}
                            sx={{
                                'color': 'background.paper',
                                'backgroundColor': alpha('#60606a', 0.6),
                                'backdropFilter': 'blur(2px)',
                                'border': `0.5px solid ${alpha('#60606a', 0.5)}`,
                                '&:hover': {
                                    backgroundColor: alpha('#60606a', 0.8),
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box
                        sx={{
                            'flex': 1,
                            'overflow': 'auto',
                            'display': 'flex',
                            'flexDirection': 'column',
                            '& > *': {
                                flex: 1,
                                minHeight: 0, // Allow flex children to shrink
                            },
                        }}
                    >
                        {loading ? <LoadingScreen /> : children}
                    </Box>
                </Box>
            </Slide>
        </Box>
    );
};
